import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FunnelEdge, FunnelNode, Storefront, flowStepToNode, nodeToFlowStep } from '../types';
import { useFunnelLocalStorage } from './useFunnelLocalStorage';

/** Max boundaries for step positions */
const MAX_X = 3200; // Max 8 steps horizontally (8 * 400)
const MAX_Y = 2640; // Max 12 steps vertically (12 * 220)

/**
 * Transform steps from API format (index, row) to frontend format (position.col, position.row)
 * Handles position initialization and boundary validation
 */
const transformStepsFromAPI = (steps: any[]) => {
  // First pass: transform basic properties with position validation
  const transformedSteps = steps.map((step: any, idx: number) => {
    // Validate and sanitize col/row values - use fallbacks for invalid data
    let col = step.index ? step.index - 1 : idx;
    let row = step.row || 0;

    // Ensure col and row are valid finite numbers within reasonable bounds
    if (typeof col !== 'number' || !isFinite(col) || col < 0 || col > 99) {
      col = idx;
    }
    if (typeof row !== 'number' || !isFinite(row) || row < 0 || row > 99) {
      row = 0;
    }

    return {
      ...step,
      label: step.name || step.label || `Page ${(step.index || idx + 1)}`,
      position: { col, row },
      flowPosition: step.flowPosition,
    };
  });

  // Second pass: Initialize flowPosition for steps that don't have it
  let entryPointIndex = 0;

  return transformedSteps.map((step) => {
    let flowPosition = step.flowPosition;

    if (!flowPosition) {
      const isStart = step.isDefault || step.conditions?.entry;

      if (isStart) {
        // Entry points: stack vertically with spacing
        flowPosition = {
          x: 0,
          y: Math.min(entryPointIndex * 400, MAX_Y),
        };
        entryPointIndex++;
      } else {
        // Non-entry points: use col/row calculation with constraints
        flowPosition = {
          x: Math.min(step.position.col * 400, MAX_X),
          y: Math.min(step.position.row * 220, MAX_Y),
        };
      }
    } else {
      // Validate existing flowPosition and constrain if out of bounds
      if (flowPosition.x > MAX_X || flowPosition.y > MAX_Y || flowPosition.x < -400 || flowPosition.y < -220) {
        const index = transformedSteps.indexOf(step);
        flowPosition = {
          x: (index % 4) * 400,
          y: Math.floor(index / 4) * 220,
        };
      }
    }

    return { ...step, flowPosition };
  });
};

/**
 * Transform steps from frontend format (position.col, position.row) to API format (index, row)
 */
export const transformStepsToAPI = (steps: any[]) => {
  return steps.map((step) => {
    const transformed: any = {
      ...step,
      name: step.label || step.name || `Page ${step.position.col + 1}`, // Map label to name with fallback
      index: step.position.col + 1, // Convert 0-based col to 1-based index
      row: step.position.row,
      position: undefined, // Remove position property
      // ✅ Keep flowPosition for Flow Editor
      flowPosition: step.flowPosition,
    };

    // Transfer conditions.entry to root-level isDefault for backend compatibility
    if (step.conditions?.entry) {
      transformed.isDefault = true;
    }

    return transformed;
  });
};

/**
 * Custom hook to manage funnel data loading, transformation, and localStorage persistence
 */
export function useFunnelData(
  apiFunnel: any,
  isFetchingFunnel: boolean,
  environment: 'staging' | 'production' = 'staging',
  funnelId?: string
) {
  const [funnel, setFunnelState] = useState<Storefront>({
    id: '',
    name: '',
    slug: '',
    abTestType: null,
    steps: [],
    nodes: [],
    edges: [],
  });
  const [originalFunnel, setOriginalFunnel] = useState<Storefront | null>(null);
  // Initialize loading state based on whether we have cached API data
  // This prevents flicker when returning with cached data (stale-while-revalidate)
  const [isLoadingData, setIsLoadingData] = useState(() => !apiFunnel);
  const [hasPendingDraft, setHasPendingDraft] = useState(false);

  // Track static resources for each step/variant
  // Structure: { stepId: { [variantId]?: { resourceType: resourceId } } }
  const [staticResources, setStaticResourcesState] = useState<Record<string, any>>({});
  // Scripts: { stepId: string } for standard steps, or { stepId: { variantId: string } } for split steps
  const [scripts, setScriptsState] = useState<Record<string, string | Record<string, string>>>({});

  // Track if we've already restored from localStorage to avoid loops
  const hasRestoredFromStorage = useRef(false);
  const isInitialLoad = useRef(true);
  // Track last processed API data to avoid unnecessary re-processing
  const lastProcessedFunnelId = useRef<string | null>(null);
  const lastProcessedUpdatedAt = useRef<string | null>(null);

  // Refs to hold latest values for saveDraft (avoids stale closures)
  const funnelRef = useRef(funnel);
  const originalFunnelRef = useRef(originalFunnel);
  const staticResourcesRef = useRef(staticResources);
  const scriptsRef = useRef<Record<string, string | Record<string, string>>>(scripts);

  // Keep refs in sync
  useEffect(() => { funnelRef.current = funnel; }, [funnel]);
  useEffect(() => { originalFunnelRef.current = originalFunnel; }, [originalFunnel]);
  useEffect(() => { staticResourcesRef.current = staticResources; }, [staticResources]);
  useEffect(() => { scriptsRef.current = scripts; }, [scripts]);

  // localStorage persistence
  const { loadDraft, saveDraft, clearDraft, getDraftAge } = useFunnelLocalStorage(funnelId, environment);

  // Initialize funnel data
  useEffect(() => {
    // Skip processing during fetching, but only show loading if we have NO data yet
    if (!apiFunnel) {
      if (!isFetchingFunnel) {
        setIsLoadingData(false);
      }
      return;
    }
    
    // We have data - never show loading state (even during background refetch)
    if (isFetchingFunnel) {
      setIsLoadingData(false);
      return;
    }
    
    // Skip re-processing if we already processed this exact data
    const currentFunnelId = apiFunnel.id;
    const currentUpdatedAt = apiFunnel.updatedAt || apiFunnel.config?.updatedAt;
    if (
      lastProcessedFunnelId.current === currentFunnelId &&
      lastProcessedUpdatedAt.current === currentUpdatedAt &&
      !isInitialLoad.current
    ) {
      setIsLoadingData(false);
      return;
    }

    // Preserve in-progress user edits: background polling (screenshot capture)
    // bumps funnel.updatedAt server-side, which would otherwise trigger a
    // destructive reload that clobbers unsaved step-config selections. Skip
    // the reload while the local funnel diverges from its loaded baseline;
    // explicit save/discard re-syncs.
    if (
      lastProcessedFunnelId.current === currentFunnelId &&
      !isInitialLoad.current &&
      originalFunnelRef.current
    ) {
      const pickEditable = (f: Storefront) => ({
        name: f.name,
        slug: f.slug,
        abTestType: f.abTestType,
        steps: f.steps,
        nodes: f.nodes,
        edges: f.edges,
      });
      const isDirty =
        JSON.stringify(pickEditable(funnelRef.current)) !==
        JSON.stringify(pickEditable(originalFunnelRef.current));
      if (isDirty) {
        setIsLoadingData(false);
        return;
      }
    }

    try {
      // Select source config based on environment
      const sourceConfig = environment === 'production'
        ? apiFunnel.productionConfig
        : apiFunnel.config;

      if (sourceConfig) {
        // 1. Get nodes and edges (Primary Source)
        // ⚠️ CRITICAL: Deep copy to prevent mutations from affecting the original API data
        // Without this, any modification to nodes/variants would mutate the React Query cache
        const nodes: FunnelNode[] = JSON.parse(JSON.stringify(sourceConfig.nodes || []));
        const edges: FunnelEdge[] = JSON.parse(JSON.stringify(sourceConfig.edges || []));


        // 2. Get steps (Legacy / Fallback)
        let transformedSteps = transformStepsFromAPI(sourceConfig.steps || []);

        // 3. Sync/Migration Logic
        // Case A: Have Nodes but No Steps (e.g. Production V2)
        // We must derive steps because 'Storefront.steps' is mandatory for legacy components
        if (nodes.length > 0 && transformedSteps.length === 0) {
          transformedSteps = nodes.map(node =>
            nodeToFlowStep(node, edges.filter(e => e.target === node.id))
          );
        }
        // Case B: Have Steps but No Nodes (Legacy Staging)
        // We must populate nodes/edges for the V2 Editor
        else if (nodes.length === 0 && transformedSteps.length > 0) {
          transformedSteps.forEach((step: any) => {
            const { node, edges: stepEdges } = flowStepToNode(step);
            nodes.push(node);
            edges.push(...stepEdges);
          });
        }

        const loadedFunnel: Storefront = {
          id: apiFunnel.id,
          name: sourceConfig.name,
          slug: sourceConfig.id || apiFunnel.id,
          abTestType: null,
          steps: transformedSteps,
          nodes,
          edges,
          productionDomains: apiFunnel.productionDomains,
          lastPromotedAt: apiFunnel.lastPromotedAt,
          updatedAt: apiFunnel.updatedAt,
          productionConfig: apiFunnel.productionConfig,
        };

        // Load static resources and scripts from metadata
        const staticResourcesFromAPI = sourceConfig.metadata?.staticResources || {};
        const scriptsFromAPI = sourceConfig.metadata?.scripts || {};

        // Check for localStorage draft on initial load
        if (isInitialLoad.current && !hasRestoredFromStorage.current) {
          const draft = loadDraft();

          if (draft) {
            // Compare only editable content (ignore read-only metadata like updatedAt, productionDomains)
            const pickEditable = (f: Storefront) => ({
              name: f.name, slug: f.slug, abTestType: f.abTestType,
              steps: f.steps, nodes: f.nodes, edges: f.edges,
            });
            const apiDataHash = JSON.stringify({ funnel: pickEditable(loadedFunnel), staticResources: staticResourcesFromAPI, scripts: scriptsFromAPI });
            const draftDataHash = JSON.stringify({ funnel: pickEditable(draft.funnel), staticResources: draft.staticResources, scripts: draft.scripts });

            if (apiDataHash !== draftDataHash) {
              // Restore from draft automatically
              hasRestoredFromStorage.current = true;
              setFunnelState(draft.funnel);
              setStaticResourcesState(draft.staticResources);
              setScriptsState(draft.scripts);
              setOriginalFunnel(JSON.parse(JSON.stringify(loadedFunnel)));
              setHasPendingDraft(true);

              const draftAge = getDraftAge();
              toast.success(`Restored unsaved changes from ${draftAge}`, { duration: 4000 });

              // Track what we processed
              lastProcessedFunnelId.current = apiFunnel.id;
              lastProcessedUpdatedAt.current = apiFunnel.updatedAt || apiFunnel.config?.updatedAt;
              
              isInitialLoad.current = false;
              return;
            }
          }
        }

        // No draft or draft matches API - use API data
        setFunnelState(loadedFunnel);
        setOriginalFunnel(JSON.parse(JSON.stringify(loadedFunnel)));
        setStaticResourcesState(staticResourcesFromAPI);
        setScriptsState(scriptsFromAPI);
        setHasPendingDraft(false);

        // Track what we processed to avoid re-processing same data
        lastProcessedFunnelId.current = apiFunnel.id;
        lastProcessedUpdatedAt.current = apiFunnel.updatedAt || apiFunnel.config?.updatedAt;
        
        isInitialLoad.current = false;
      }
      // Note: Missing production config is expected for new funnels
    } catch {
      toast.error('Failed to load funnel');
    } finally {
      setIsLoadingData(false);
    }
  }, [apiFunnel, isFetchingFunnel, environment, loadDraft, getDraftAge]);

  const hasUnsavedChanges = useMemo(() => {
    if (!originalFunnel) return false;
    const pick = (f: Storefront) => ({
      name: f.name,
      slug: f.slug,
      abTestType: f.abTestType,
      steps: f.steps,
      nodes: f.nodes,
      edges: f.edges,
    });
    return JSON.stringify(pick(funnel)) !== JSON.stringify(pick(originalFunnel));
  }, [funnel, originalFunnel]);

  /**
   * Wrapper for setFunnel that also triggers localStorage save
   * Uses refs to avoid stale closures when multiple updates happen rapidly
   */
  const setFunnel = useCallback((newFunnel: Storefront | ((prev: Storefront) => Storefront)) => {
    setFunnelState((prev) => {
      const updated = typeof newFunnel === 'function' ? newFunnel(prev) : newFunnel;
      // Auto-save to localStorage using refs for latest values (debounced in the hook)
      saveDraft(updated, staticResourcesRef.current, scriptsRef.current);
      return updated;
    });
  }, [saveDraft]);

  /**
   * Wrapper for setStaticResources that also triggers localStorage save
   */
  const setStaticResources = useCallback((newResources: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => {
    setStaticResourcesState((prev) => {
      const updated = typeof newResources === 'function' ? newResources(prev) : newResources;
      saveDraft(funnelRef.current, updated, scriptsRef.current);
      return updated;
    });
  }, [saveDraft]);

  /**
   * Wrapper for setScripts that also triggers localStorage save
   */
  const setScripts = useCallback((newScripts: Record<string, string | Record<string, string>> | ((prev: Record<string, string | Record<string, string>>) => Record<string, string | Record<string, string>>)) => {
    setScriptsState((prev) => {
      const updated = typeof newScripts === 'function' ? newScripts(prev) : newScripts;
      saveDraft(funnelRef.current, staticResourcesRef.current, updated);
      return updated;
    });
  }, [saveDraft]);

  /**
   * Update funnel after successful save - clears localStorage draft
   */
  const updateFunnelFromResult = useCallback((resultFunnel: any) => {
    if (resultFunnel?.config) {
      const transformedSteps = transformStepsFromAPI(resultFunnel.config.steps || []);

      const updatedFunnel: Storefront = {
        id: resultFunnel.id,
        name: resultFunnel.config.name,
        slug: resultFunnel.config.id || resultFunnel.id,
        abTestType: null,
        steps: transformedSteps,
        nodes: resultFunnel.config.nodes || funnel.nodes,
        edges: resultFunnel.config.edges || funnel.edges,
        productionDomains: resultFunnel.productionDomains,
        lastPromotedAt: resultFunnel.lastPromotedAt,
        updatedAt: resultFunnel.updatedAt,
      };

      setFunnelState(updatedFunnel);
      setOriginalFunnel(JSON.parse(JSON.stringify(updatedFunnel)));
      setHasPendingDraft(false);

      // Clear localStorage draft after successful save
      clearDraft();
    }
  }, [funnel.nodes, funnel.edges, clearDraft]);

  /**
   * Discard changes - clears localStorage draft
   */
  const discardChanges = useCallback(() => {
    if (originalFunnel) {
      setFunnelState(JSON.parse(JSON.stringify(originalFunnel)));
      setHasPendingDraft(false);

      // Clear localStorage draft on discard
      clearDraft();

      toast.success('Changes discarded');
    }
  }, [originalFunnel, clearDraft]);

  return {
    funnel,
    setFunnel,
    originalFunnel,
    isLoadingData,
    hasUnsavedChanges,
    hasPendingDraft,
    updateFunnelFromResult,
    discardChanges,
    staticResources,
    setStaticResources,
    scripts,
    setScripts,
    clearDraft,
  };
}

